import React, { useEffect, useState, useCallback } from 'react';
import { Search, Loader2, Plus, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { CompanyPicker } from './CRMPickers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_STATUSES = ["Active", "Cold", "Bounced", "Do Not Contact"];

const CRMContacts = ({ embedded = false }) => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dmOnly, setDmOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 200 });
    if (search) params.set('search', search);
    if (dmOnly) params.set('is_decision_maker', 'true');
    if (overdueOnly) params.set('overdue_only', 'true');
    fetchWithAuth(`${API_URL}/api/crm/contacts?${params.toString()}`)
      .then(d => { setContacts(d.contacts); setTotal(d.total); }).catch(() => {}).finally(() => setLoading(false));
  }, [search, dmOnly, overdueOnly]);

  useEffect(() => { load(); }, [load]);

  const isOverdue = (c) => c.next_followup_date && c.next_followup_date <= new Date().toISOString().slice(0, 10);

  return (
    <IntelligenceLayout title="Contacts" embedded={embedded}>
      <div className="p-6 space-y-4" data-testid="crm-contacts-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Contacts</h1>
            <p className="text-sm text-gray-500 mt-1">{total.toLocaleString()} contacts across all accounts</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-contact-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 transition-colors">
            <Plus className="h-4 w-4" /> New Contact
          </button>
        </div>

        <div className="flex items-center flex-wrap gap-2" data-testid="crm-contacts-filters">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email..." data-testid="contact-search-input"
              className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg w-64 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
          </div>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="dm-only-toggle">
            <input type="checkbox" checked={dmOnly} onChange={e => setDmOnly(e.target.checked)} className="rounded" /> Decision-makers only
          </label>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-2 cursor-pointer" data-testid="contacts-overdue-toggle">
            <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} className="rounded" /> Overdue follow-up
          </label>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-gray-400 p-8 text-center">No contacts match these filters.</p>
          ) : (
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2.5 px-4">Name</th>
                  <th className="text-left py-2.5 px-4">Title</th>
                  <th className="text-left py-2.5 px-4">Company</th>
                  <th className="text-left py-2.5 px-4">Email</th>
                  <th className="text-left py-2.5 px-4">Decision Maker</th>
                  <th className="text-left py-2.5 px-4">Status</th>
                  <th className="text-left py-2.5 px-4">Next Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id} onClick={() => setSelected(c)} data-testid={`contact-row-${c.id}`}
                    className="border-t border-gray-100 hover:bg-[#fdf9ef] cursor-pointer transition-colors">
                    <td className="py-2.5 px-4 font-medium text-[#0A1F44]">{c.first_name} {c.last_name || ''}</td>
                    <td className="py-2.5 px-4 text-gray-500">{c.title || '—'}</td>
                    <td className="py-2.5 px-4 text-gray-600">{c.company_name || '—'}</td>
                    <td className="py-2.5 px-4 text-gray-500">{c.email || '—'}</td>
                    <td className="py-2.5 px-4">{c.is_decision_maker ? <span className="text-[10px] px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#8a6d1a] rounded font-medium">Yes</span> : '—'}</td>
                    <td className="py-2.5 px-4 text-gray-500">{c.contact_status || '—'}</td>
                    <td className={`py-2.5 px-4 ${isOverdue(c) ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{c.next_followup_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showCreate && <CreateContactModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {selected && <ContactDetailModal contact={selected} onClose={() => setSelected(null)} onUpdated={() => { setSelected(null); load(); }} />}
    </IntelligenceLayout>
  );
};

const CreateContactModal = ({ onClose, onCreated }) => {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', title: '', email: '', phone: '', linkedin_url: '', notes: '', is_decision_maker: false });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!company) { toast.error('Select a company'); return; }
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, company_id: company.id }),
      });
      toast.success('Contact created');
      onCreated();
    } catch (e) {
      toast.error('Could not create contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="create-contact-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2"><User className="h-4 w-4" /> New Contact</h3>
          <button onClick={onClose} data-testid="close-create-contact-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <CompanyPicker value={company} onChange={setCompany} testId="contact-company-picker" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name *"
            data-testid="create-contact-first-name" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name"
            data-testid="create-contact-last-name" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Job title"
          data-testid="create-contact-title" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email"
            data-testid="create-contact-email" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone"
            data-testid="create-contact-phone" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <input value={form.linkedin_url} onChange={e => setForm({ ...form, linkedin_url: e.target.value })} placeholder="LinkedIn URL"
          data-testid="create-contact-linkedin" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes / source" rows={2}
          data-testid="create-contact-notes" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={form.is_decision_maker} onChange={e => setForm({ ...form, is_decision_maker: e.target.checked })} data-testid="create-contact-decision-maker" />
          Decision maker
        </label>
        <button onClick={submit} disabled={saving} data-testid="create-contact-submit"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Contact'}
        </button>
      </div>
    </div>
  );
};

export default CRMContacts;

const ContactDetailModal = ({ contact, onClose, onUpdated }) => {
  const [form, setForm] = useState({ ...contact });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      await fetchWithAuth(`${API_URL}/api/crm/contacts/${contact.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name, last_name: form.last_name, title: form.title,
          email: form.email, phone: form.phone, linkedin_url: form.linkedin_url,
          notes: form.notes, is_decision_maker: form.is_decision_maker, contact_status: form.contact_status,
        }),
      });
      toast.success('Contact updated');
      onUpdated();
    } catch (e) {
      toast.error('Could not update contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="contact-detail-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2"><User className="h-4 w-4" /> Edit Contact</h3>
          <button onClick={onClose} data-testid="close-contact-detail-modal"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <p className="text-xs text-gray-400">{contact.company_name || '—'}</p>
        <div className="grid grid-cols-2 gap-2">
          <input value={form.first_name || ''} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="First name *"
            data-testid="contact-detail-first-name" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.last_name || ''} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name"
            data-testid="contact-detail-last-name" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Job title"
          data-testid="contact-detail-title" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email"
            data-testid="contact-detail-email" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone"
            data-testid="contact-detail-phone" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        </div>
        <input value={form.linkedin_url || ''} onChange={e => setForm({ ...form, linkedin_url: e.target.value })} placeholder="LinkedIn URL"
          data-testid="contact-detail-linkedin" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <textarea value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes / source" rows={2}
          data-testid="contact-detail-notes" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={!!form.is_decision_maker} onChange={e => setForm({ ...form, is_decision_maker: e.target.checked })} data-testid="contact-detail-decision-maker" />
          Decision maker
        </label>
        <div>
          <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1 block">Contact Status</label>
          <select value={form.contact_status || 'Active'} onChange={e => setForm({ ...form, contact_status: e.target.value })} data-testid="contact-detail-status"
            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg">
            {CONTACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {form.contact_status === 'Do Not Contact' && (
            <p className="text-[11px] text-amber-600 mt-1" data-testid="contact-detail-dnc-warning">Automated outreach to this contact will be blocked.</p>
          )}
        </div>
        <button onClick={submit} disabled={saving} data-testid="save-contact-detail"
          className="w-full py-2 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};
